/**
 * Modern 现代模板 - 列表页
 * 
 * 风格特点：
 * - 左侧固定筛选侧边栏 + 右侧网格内容区
 * - 双列/三列卡片网格
 * - 顶部搜索条 + 排序切换
 * - 分页在底部居中
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";
import { useTemplateColors } from "@/hooks/useTemplateColors";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "popular", label: "浏览最多", icon: "ri-fire-line" },
];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

export default function ModernCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  const colors = useTemplateColors();

  // 数据状态
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 筛选状态
  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);

  const PAGE_SIZE = 12;

  // 加载分类
  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  // 根据路由 slug 设置当前分类
  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  // 加载列表数据
  useEffect(() => {
    if (siteLoading || !siteId) return;

    setLoading(true);
    const params: Record<string, any> = {
      page: currentPage,
      page_size: PAGE_SIZE,
    };
    if (activeCategoryId) params.category_id = activeCategoryId;

    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  // 当前分类名称
  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // 排序后的列表
  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") {
      list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    }
    return list;
  }, [listings, sortBy]);

  const handleCategoryClick = (catId: string, catSlug: string) => {
    setActiveCategoryId(catId === activeCategoryId ? "" : catId);
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* ── 页面标题栏 ── */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 border-b border-blue-500/20 sticky top-[60px] z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-3 flex-wrap">
          {/* 移动端菜单按钮 */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-black/10 transition-colors cursor-pointer"
          >
            <i className="ri-filter-3-line text-lg text-white" />
          </button>

          {/* 面包屑 */}
          <div className="hidden sm:flex items-center gap-1.5 text-sm">
            <Link to="/" className="text-blue-200 hover:text-white transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs text-blue-300" />
            <span className="text-white font-medium">{currentCategoryName}</span>
          </div>

          {/* 结果数 */}
          <span className="text-xs text-blue-200 hidden md:inline ml-auto whitespace-nowrap">共 {total} 条</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex gap-6">
        {/* ════════ 左侧筛选栏 ════════ */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-40 w-72 bg-white shadow-xl lg:shadow-none transform transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:w-56 shrink-0
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          {/* 移动端关闭按钮 */}
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden absolute top-3 right-3 p-2 rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg text-gray-500" />
          </button>

          <div className="h-full overflow-y-auto p-5 lg:p-0 pt-14 lg:pt-0">
            {/* 分类筛选 */}
            <div className="lg:sticky lg:top-[120px]">
              <h3 className="text-xs font-bold text-blue-500 uppercase tracking-wider mb-3 px-1">服务分类</h3>
              <nav className="space-y-0.5">
                <button
                  onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                    !activeCategoryId
                      ? `${colors.bgLight} ${colors.textLight}`
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className="ri-apps-line text-base opacity-60" />
                    全部分类
                  </span>
                </button>
                {(navCategories.length > 0 ? navCategories : categories).map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id, cat.slug)}
                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      activeCategoryId === cat.id
                        ? `${colors.bgLight} ${colors.textLight}`
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <i className={`${cat.icon || 'ri-folder-line'} text-base opacity-60`} />
                      {cat.name}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </aside>

        {/* 移动端遮罩 */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* ════════ 右侧主内容区 ════════ */}
        <main className="flex-1 min-w-0">
          {/* 工具栏：排序 + 视图 */}
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800">{currentCategoryName}</h2>
            <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg p-0.5">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer ${
                    sortBy === opt.key
                      ? `bg-white ${colors.text} shadow-sm`
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className={opt.icon} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 信息卡片网格 */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...Array(6)].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl overflow-hidden border border-gray-100">
                  <div className="w-full h-40 bg-gray-200" />
                  <div className="p-4 space-y-2.5">
                    <div className="w-3/4 h-5 bg-gray-200 rounded-lg" />
                    <div className="w-1/2 h-3.5 bg-gray-150 rounded" />
                    <div className="w-full h-3 bg-gray-100 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : sortedListings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedListings.map(listing => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className={`group bg-white rounded-2xl border border-gray-100 ${colors.cardHoverBorder} hover:shadow-lg transition-all duration-300 overflow-hidden`}
                >
                  {/* 图片区 */}
                  <div className="relative w-full h-44 bg-gradient-to-br from-slate-100 to-slate-50 overflow-hidden">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-gray-300" />
                      </div>
                    )}
                    {/* 置顶标签 */}
                    {listing.is_pinned && (
                      <span className={`absolute top-3 left-3 px-2 py-0.5 ${colors.badgeBg} text-white text-xs font-semibold rounded-md shadow-sm`}>
                        置顶
                      </span>
                    )}
                    {/* 分类标签 */}
                    <span className="absolute bottom-3 right-3 px-2 py-0.5 bg-black/50 backdrop-blur-sm text-white text-xs rounded-md">
                      {listing.category?.name}
                    </span>
                  </div>

                  {/* 内容区 */}
                  <div className="p-4">
                    <h3 className={`font-semibold text-gray-800 text-sm line-clamp-1 ${colors.cardHoverText} transition-colors mb-1.5`}>
                      {listing.title}
                    </h3>
                    <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3">{listing.content}</p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-user-line text-xs text-gray-400 shrink-0" />
                        <span className="truncate max-w-[70px]">{listing.contact_name || '匿名'}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span>{formatDate(listing.created_at)}</span>
                        <span className="flex items-center gap-0.5"><i className="ri-eye-line" />{listing.view_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
              <div className={`w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl ${colors.bgLight}`}>
                <i className={`ri-search-line text-2xl ${colors.placeholderIcon}`} />
              </div>
              <p className="text-gray-500 font-medium">暂无相关信息</p>
              <p className="text-gray-400 text-sm mt-1">试试其他关键词或分类</p>
            </div>
          )}

          {/* 分页 */}
          {!loading && totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 ${colors.borderLight} ${colors.textHover} disabled:opacity-40 transition-colors cursor-pointer`}
              >
                <i className="ri-arrow-left-s-line" />
              </button>
              {[...Array(Math.min(7, totalPages))].map((_, i) => {
                let p: number;
                if (totalPages <= 7) p = i + 1;
                else if (currentPage <= 4) p = i + 1;
                else if (currentPage >= totalPages - 3) p = totalPages - 6 + i;
                else p = currentPage - 3 + i;

                return (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      currentPage === p
                        ? `${colors.bg} text-white shadow-sm`
                        : `border border-gray-200 text-gray-600 ${colors.borderLight} ${colors.textHover}`
                    }`}
                  >{p}</button>
                );
              })}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className={`w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 ${colors.borderLight} ${colors.textHover} disabled:opacity-40 transition-colors cursor-pointer`}
              >
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
}
